// src/homepage/Footer.tsx
import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';

const FooterContainer = styled.footer`
    background: #000000;
    color: white;
    padding: 1.5rem 0 1rem;
    position: relative;
    overflow: hidden;
`;

const BackgroundPattern = styled.div`
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    opacity: 0.05;
    background-image: radial-gradient(circle at 25% 25%, #990147 2px, transparent 0),
    radial-gradient(circle at 75% 75%, #990147 2px, transparent 0);
    background-size: 50px 50px;
`;

const FooterContent = styled.div`
    max-width: 1000px;
    margin: 0 auto;
    padding: 0 2rem;
    position: relative;
    z-index: 2;
`;

const FooterGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 2rem;
    margin-bottom: 1rem;

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
        gap: 1rem;
    }
`;

const FooterSection = styled(motion.div)`
    h3 {
        font-size: 1.1rem;
        font-weight: 700;
        margin-bottom: 1rem;
        color: white;
        position: relative;
        padding-bottom: 0.5rem;

        &::after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 0;
            width: 40px;
            height: 2px;
            background: linear-gradient(135deg, #990147 0%, #cc3366 100%);
            border-radius: 2px;
        }
    }
`;

const ContactInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
`;

const ContactItem = styled(motion.div)`
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;

    .icon {
        width: 18px;
        height: 18px;
        color: #990147;
        flex-shrink: 0;
        margin-top: 0.1rem;
        font-size: 0.9rem;
        display: flex;
        align-items: center;
        justify-content: center;
    }

    .content {
        flex: 1;

        .label {
            font-weight: 600;
            color: white;
            margin-bottom: 0.2rem;
            display: block;
            font-size: 0.85rem;
        }

        .value {
            opacity: 0.8;
            line-height: 1.4;
            font-size: 0.85rem;
        }

        a {
            color: inherit;
            text-decoration: none;
            transition: all 0.3s ease;

            &:hover {
                opacity: 0.8;
            }
        }
    }
`;

const HoursSection = styled.div`
    .hours {
        opacity: 0.8;
        line-height: 1.4;
        margin-bottom: 0.5rem;
        font-size: 0.85rem;
    }

    .note {
        font-size: 0.75rem;
        opacity: 0.7;
        line-height: 1.3;
        font-style: italic;
        background: rgba(153, 1, 71, 0.1);
        padding: 0.5rem;
        border-radius: 6px;
        border: 1px solid rgba(153, 1, 71, 0.2);
    }
`;
/* ← NOVÝ LINK (jediná zmena) */
const PrivacyLink = styled(motion.a)`
    font-size: 0.8rem;
    color: rgba(255, 255, 255, 0.7);
    text-decoration: none;
    margin-left: 2rem;

    &:hover {
        color: #cc3366;
    }

    @media (max-width: 640px) {
        margin-left: 0;
        margin-top: 0.5rem;
    }
`;

const FooterBottom = styled.div`
    border-top: 1px solid rgba(255, 255, 255, 0.15);
    padding-top: 0.75rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 0.5rem;

    @media (max-width: 640px) {
        flex-direction: column;
        text-align: center;
        gap: 0.25rem;
    }
`;

const Copyright = styled(motion.p)`
    opacity: 0.7;
    margin: 0;
    font-size: 0.8rem;
    font-weight: 500;
`;

const CompanyInfo = styled(motion.div)`
    text-align: right;

    @media (max-width: 640px) {
        text-align: center;
    }

    .company-name {
        font-weight: 600;
        color: white;
        margin-bottom: 0.1rem;
        font-size: 0.8rem;
    }

    .company-details {
        opacity: 0.7;
        font-size: 0.75rem;
    }
`;

const Footer: React.FC = () => {
    return (
        <FooterContainer>
            <BackgroundPattern />
            <FooterContent>
                <FooterGrid>
                    {/* Contact Information */}
                    <FooterSection
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        viewport={{ once: true, margin: '-50px' }}
                    >
                        <h3>Kontakt</h3>
                        <ContactInfo>
                            <ContactItem
                                whileHover={{ x: 3 }}
                                transition={{ duration: 0.2 }}
                            >
                                <div className="icon">📧</div>
                                <div className="content">
                                    <span className="label">Email</span>
                                    <div className="value">
                                        <a href="mailto:sonabelanska@gmail.com">
                                            sonabelanska@gmail.com
                                        </a>
                                    </div>
                                </div>
                            </ContactItem>

                            <ContactItem
                                whileHover={{ x: 3 }}
                                transition={{ duration: 0.2 }}
                            >
                                <div className="icon">📍</div>
                                <div className="content">
                                    <span className="label">Adresa</span>
                                    <div className="value">
                                        Panónska cesta 17, Bratislava
                                    </div>
                                </div>
                            </ContactItem>
                        </ContactInfo>
                    </FooterSection>

                    {/* Hours Section - Moved to the right */}
                    <FooterSection
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        viewport={{ once: true, margin: '-50px' }}
                    >
                        <h3>Ordinačné hodiny</h3>
                        <HoursSection>
                            <div className="hours">
                                Pondelok - štvrtok: zatvorené
                            </div>
                            <div className="hours">
                                Piatok: 8.00-16.00 hod.
                            </div>
                            <div className="note">
                                Posledný pacient je prijatý o 15.00 hod.
                                Konzultácie a vyšetrenia sú možné iba po predchádzajúcom objednaní.
                            </div>
                        </HoursSection>
                    </FooterSection>
                </FooterGrid>

                <FooterBottom>
                    <Copyright
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        viewport={{ once: true }}
                    >
                        © {new Date().getFullYear()} MUDr. Soňa Belanská
                    </Copyright>
                    {/* ← TU JE JEDINÁ PRIDANÁ VEC */}
                    <PrivacyLink
                        href="/ochrana-osobnych-udajov"
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        transition={{ duration: 0.6, delay: 0.3 }}
                        viewport={{ once: true }}
                    >
                        Ochrana osobných údajov
                    </PrivacyLink>
                    <CompanyInfo
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        transition={{ duration: 0.6, delay: 0.3 }}
                        viewport={{ once: true }}
                    >
                        <div className="company-name">SB Consulting Slovakia, s.r.o.</div>
                        <div className="company-details">Všetky práva vyhradené</div>
                    </CompanyInfo>
                </FooterBottom>
            </FooterContent>
        </FooterContainer>
    );
};

export default Footer;